import { Quote, Youtube } from '@cinch-labs/design-system'
import type { ArticleStoryContent } from '@cinch-nx/landing-ui-articles'
import { ArticleHeader } from '@cinch-nx/landing-ui-articles'
import {
  Head,
  JsonLdScript,
  formatHowToSchema,
} from '@cinch-nx/landing-ui-shared'

import { StoryData, useStoryblokState } from '@cinch-nx/data-storyblok'
import { JargonPageStoryContent } from '@cinch-nx/landing-feature-jargon-hub'
import { Wrapper } from '@cinch-nx/shared-ui'

import cx from 'classnames'
import styles from './articles.module.css'
import { ArticleBody, ArticleRelatedJargon, LeadParagraph } from './components'
import { ArticlesFeatureProps } from './get-static-props'

export const ArticlesFeature = ({ story, preview }: ArticlesFeatureProps) => {
  const typedStory = story as StoryData<ArticleStoryContent> & {
    rels: unknown[]
  }
  const { content } = useStoryblokState({
    story: typedStory,
    options: {
      resolveRelations: ['ArticleTemplate.relatedJargon'],
    },
    enableBridge: preview,
  })

  const {
    authorByline,
    title,
    subTitle,
    video,
    image,
    leadParagraph,
    quote,
    body,
    SEO,
  } = content as ArticleStoryContent

  const seoDescription = SEO && SEO[0]?.description

  const hasRelatedJargon = typedStory.rels && typedStory.rels?.length > 0
  return (
    <>
      <Head
        {...SEO}
        fallbackTitle={`${title} - cinch`}
        fallbackDescription={seoDescription || subTitle}
      />
      <article className={styles.page} data-testid="article-wrapper">
        {SEO &&
          SEO[0]?.HowTo &&
          Array.isArray(SEO[0]?.HowTo) &&
          SEO[0]?.HowTo.length > 0 && (
            <JsonLdScript
              schema={formatHowToSchema(SEO[0].HowTo)}
              tagId="HowToPage"
            />
          )}
        <Wrapper isSlim>
          <ArticleHeader
            authorByline={{
              block: authorByline,
              publishedDate: story?.published_at,
            }}
            title={title}
            subTitle={subTitle}
            video={video}
            image={image}
          ></ArticleHeader>
          <div className={styles.content}>
            <div
              className={cx(styles.main, {
                [styles.mainNoPadding]: !hasRelatedJargon,
              })}
            >
              {video?.length > 0 ? (
                <Youtube
                  videoId={video[0].videoId}
                  title={video[0].title}
                  buttonLabel={video[0].buttonLabel}
                  className={styles.video}
                  customThumbnail={
                    video[0].thumbnail.filename !== ''
                      ? video[0].thumbnail.filename
                      : image.filename
                  }
                  videoUrlOptions={{ autoplay: 1 }}
                  showNewPlayIcon
                />
              ) : null}

              <LeadParagraph body={leadParagraph} />
              {quote ? <Quote text={quote} /> : null}
              <ArticleBody body={body} />
            </div>
            {hasRelatedJargon && (
              <aside className={styles.jargonBuster}>
                <ArticleRelatedJargon
                  items={typedStory.rels as StoryData<JargonPageStoryContent>[]}
                />
              </aside>
            )}
          </div>
        </Wrapper>
      </article>
    </>
  )
}
